import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import getScrollParent from './getScrollParent';

/**
 * Scroll offsets per history entry (location.key, optionally scoped for pages
 * with several scrollers, like the mobile home tabs). In-memory only: after a
 * full reload the Redux content is gone too, and history keys regenerate, so
 * persisted offsets could never match anything.
 */
const offsets = new Map<string, number>();
const MAX_ENTRIES = 50;

const save = (key: string, value: number): void => {
  if (!offsets.has(key) && offsets.size >= MAX_ENTRIES) {
    // Evict the oldest insertion to bound memory.
    offsets.delete(offsets.keys().next().value as string);
  }
  offsets.set(key, value);
};

/**
 * Re-apply `target` once the content is tall enough for it to stick. Never
 * clamps to the current bottom — a clamped jump would put the infinite-scroll
 * sentinel in range and cascade page loads. Cancels on user input so it never
 * fights someone who starts scrolling before the restore lands.
 */
const restoreTo = (el: HTMLElement, target: number): (() => void) => {
  let raf = 0;
  const deadline = performance.now() + 5000;
  const cancel = () => {
    window.cancelAnimationFrame(raf);
    ['wheel', 'touchstart', 'keydown'].forEach((t) => el.removeEventListener(t, cancel));
  };
  const tick = () => {
    if (el.scrollHeight - el.clientHeight >= target) {
      el.scrollTop = target;
      cancel();
      return;
    }
    if (performance.now() > deadline) {
      cancel(); // give up: content shrank while away; stay at top
      return;
    }
    raf = window.requestAnimationFrame(tick);
  };
  ['wheel', 'touchstart', 'keydown'].forEach((t) => el.addEventListener(t, cancel, { passive: true }));
  tick();
  return cancel;
};

/**
 * Keeps a list page's scroll position across history back/forward.
 *
 * Attach the returned ref to an element rendered in EVERY branch of the page
 * (including loading/error states) so the save listener binds at mount; the
 * hook finds the real scroll container the same way the infinite-scroll
 * sentinel does. On POP navigation the last offset for this history entry is
 * restored once the content is tall enough.
 */
export const useScrollRestoration = <T extends HTMLElement = HTMLDivElement>(scope = '') => {
  const anchorRef = useRef<T | null>(null);
  const { key: locationKey } = useLocation();
  const navigationType = useNavigationType();
  const key = scope ? `${locationKey}:${scope}` : locationKey;

  useLayoutEffect(() => {
    const el = getScrollParent(anchorRef.current);
    if (!el) return undefined;

    const onScroll = () => save(key, el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });

    const saved = navigationType === 'POP' ? offsets.get(key) : undefined;
    const cancelRestore = saved ? restoreTo(el, saved) : undefined;

    return () => {
      el.removeEventListener('scroll', onScroll);
      cancelRestore?.();
    };
    // navigationType is deliberately read only when the entry (key) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return anchorRef;
};

/**
 * Forward navigations start at the top. Needed because the layout scroller
 * persists across routes, so a push (feed → post) would otherwise carry the
 * feed's offset onto the new page. POPs are left alone for restoration.
 */
export const useScrollTopOnPush = (scrollEl: HTMLElement | null): void => {
  const { key } = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (navigationType !== 'POP' && scrollEl) {
      scrollEl.scrollTop = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, scrollEl]);
};
